import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Table, Tag, Button, Tooltip, Popover, Select } from 'antd';
import UIComponent from './uiComponent';
import { DynamicIcon } from './utils';
import { ulid } from 'ulidx';
import UIValidation from './uiValidation';
import UIValue from './uiValue';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getEnvVariable } from './utils/env';
const tableStyles = `
  .ant-pagination {
    margin-bottom: 0px !important;
  }
  .ant-table-thead > tr:first-of-type > .ant-table-cell.column-group-outline {
    border: 1px solid #ccc !important;
  }
  .ant-table-thead > tr:first-of-type > th.ant-table-cell.column-group-outline:not(:first-child) {
    border-left: none !important;
  }
  .ant-table-thead > tr:first-of-type > th.ant-table-cell.column-group-outline:first-child {
    border-left: 1px solid #ccc !important;
  }
`;
const { Column, ColumnGroup } = Table;
function UITableComponent(props) {
    function columnStructure(col) {
        if (col.type === 'date') {
            return (_jsx(Column, { title: col.text, dataIndex: col.dataIndex, render: (dateStr) => {
                    const date = new Date(dateStr);
                    return _jsx("span", { children: date.toLocaleDateString() });
                } }, col.name));
        }
        else if (col.type === 'currency') {
            return (_jsx(Column, { title: col.text, dataIndex: col.dataIndex, render: (currency) => {
                    if (!currency) {
                        return null;
                    }
                    const value = currency.toFixed(2);
                    return _jsxs("span", { children: ["$", value] });
                } }, col.name));
        }
        else if (col.type === 'dropDown') {
            /*
             * We use `split('-')[0]` to extract just the display label and drop the suffix,
             * which is the column ID used to target the specific row when updating.
             *
             * Example:
             *   const raw = "Adjusted Closing Share Price-1";
             *   // raw.split('-')[0] === "Adjusted Closing Share Price"
             *   // suffix "1" matches the Row ID for filtering and updating that row
             *
             * This workaround is needed because our current table setup only exposes
             * the single cell’s value (dataIndex) in the renderer—not the full row data—
             * so we encode the row identifier as the suffix to know which record to update.
             */
            return (_jsx(Column, { title: col.text, dataIndex: col.dataIndex, render: (currentValue) => {
                    const value = currentValue.split('-')[0];
                    return (_jsx(Select, { value: value, style: { width: 220 }, onChange: (newValue) => {
                            col.onChange?.(newValue, currentValue);
                        }, children: col.options?.map((opt) => (_jsx(Select.Option, { value: opt, children: opt }, opt))) }));
                } }, col.name));
        }
        else if (col.type === 'link') {
            return (_jsx(Column, { title: col.text, dataIndex: col.dataIndex, render: (text) => {
                    if (!text)
                        return _jsx(Tag, { color: "red", children: "None" });
                    return (_jsx(Tag, { color: "blue", children: _jsx("a", { href: `${getEnvVariable('API_URI')?.replace(/\/+$/, '') ?? ''}${text}`, target: "_blank", rel: "noopener noreferrer", children: col.text }) }));
                } }, col.name));
        }
        else if (col.type === 'button') {
            /*
             *  Use a dynamic icon in the format overview to trigger primary-template updates.
             * - if this column is our special “dynamicIcon” column by checking dataIndex.
             * - For dynamic columns, the cell’s `text` is encoded as `"<iconName>-<formatId>-<orgId>"`.
             *   We call `split('-')[0]` to extract just the iconName to render, and leave the suffix
             *   intact so that onClick(text) can parse out the formatId and orgId when handling updates.
             *
             * - Static buttons (col.iconType) use a fixed red background and white icon;
             *   dynamic buttons switch to white background + orange icon and zero padding
             *   so the icon itself is the only visible element.
             *
             * Why this workaround?
             * Our table only provides the single cell value (`dataIndex`) to `render()` and not the full row.
             * To know which row to act upon, we encode the row identifier and some value as a suffix on that cell’s text.
             * Splitting on `-` gives us both the display/icon name and a way to filter/update the correct record.
             *
             * Example:
             *   raw text = "edit-3-4"
             *   iconName = raw.split('-')[0]  // "edit"
             *   suffix   = raw.split('-')[1]  // "formatId"
             *   suffix   = raw.split('-')[2]  // "orgId"
             */
            const isDynamicColumn = col.dataIndex === 'dynamicIcon';
            return (_jsx(Column, { title: _jsx("div", { style: { textAlign: 'center' }, children: col.text }), dataIndex: col.dataIndex, render: (text) => {
                    const iconName = isDynamicColumn && typeof text === 'string' ? text.split('-')[0] : col.iconType;
                    return (_jsx("div", { style: { display: 'flex', justifyContent: 'center' }, children: _jsx(Tooltip, { title: col.text, children: _jsx(Button, { type: 'primary', style: {
                                    backgroundColor: !isDynamicColumn ? '#c00000' : 'white',
                                    color: isDynamicColumn ? '#FFA500' : 'white',
                                    ...(isDynamicColumn && {
                                        border: 'none',
                                        boxShadow: 'none',
                                        padding: 0,
                                        minWidth: 'auto',
                                        height: 'auto',
                                        lineHeight: 'inherit',
                                    }),
                                }, onClick: () => col.onClick?.(text), shape: "circle", icon: _jsx(DynamicIcon, { iconName: iconName }) }) }) }));
                } }, col.name));
        }
        else if (col.type === 'tags') {
            return (_jsx(Column, { title: _jsxs("p", { style: {
                        display: 'flex',
                        gap: '5px',
                    }, children: [col.text, col.tooltip && (_jsx(Tooltip, { title: col.tooltip, children: _jsx(InfoCircleOutlined, { style: { cursor: 'pointer' } }) }))] }), dataIndex: col.dataIndex, render: (text) => {
                    const MAX_VISIBLE_TAGS = 3;
                    const tags = text.split(' ');
                    const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
                    const hiddenTags = tags.slice(MAX_VISIBLE_TAGS);
                    const tagComponents = visibleTags.map((t) => _jsx(Tag, { color: "green", children: t }));
                    return (_jsxs("div", { style: {
                            display: 'flex',
                            maxWidth: '350px',
                            flexWrap: 'wrap',
                            gap: '10px',
                        }, children: [tagComponents, hiddenTags && hiddenTags.length > 0 && (_jsx(Popover, { content: _jsx("div", { style: { maxWidth: '200px' }, children: hiddenTags.map((t, index) => (_jsx(Tag, { color: "green", children: t }, index + MAX_VISIBLE_TAGS))) }), title: "More Values", children: _jsx(Tag, { color: "blue", style: { cursor: 'pointer' }, children: `+${hiddenTags.length} more` }) }))] }));
                } }, col.name));
        }
        else {
            return _jsx(Column, { title: col.text, dataIndex: col.dataIndex }, col.name);
        }
    }
    const columns = [];
    for (const col of props.columns) {
        if (col.type === 'group') {
            const colGroup = (_jsx(ColumnGroup, { className: 'column-group-outline', title: _jsx("p", { style: { fontWeight: 'bold' }, children: col.text }), children: col.children.map((child) => columnStructure(child)) }));
            columns.push(colGroup);
        }
        else {
            columns.push(columnStructure(col));
        }
    }
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: tableStyles }), _jsx(Table, { dataSource: props.datasource, scroll: { x: true }, children: columns }, ulid())] }));
}
class UIColumn extends UIComponent {
    type;
    text;
    iconType;
    tooltip;
    options;
    constructor(s) {
        const text = Object.keys(s)[0];
        const col = s[text];
        super(col);
        this.text = text;
        this.iconType = col.iconType;
        this.type = col.type;
        this.dataIndex = col.dataIndex;
        this.tooltip = col.tooltip ? col.tooltip : undefined;
        if (this.type === 'group') {
            col.columns.forEach((childCol) => {
                this.children.push(new UIColumn(childCol));
            });
        }
        this.options = [];
        if ('options' in col) {
            for (const o of col.options && col.options) {
                this.options.push(o);
            }
        }
    }
    getComponent() {
        return null;
    }
}
export default class UITable extends UIValue {
    columns;
    datasource;
    setDatasource(datasource) {
        this.datasource = datasource;
        this.value = datasource;
        this.error = undefined;
    }
    setColumns(columns) {
        this.columns = columns;
    }
    constructor(s) {
        super(s);
        this.columns = [];
        this.datasource = [];
        this.value = [];
        if ('columns' in s) {
            const columns = s['columns'];
            Object.keys(columns).forEach((key) => {
                this.columns.push(new UIColumn(columns[key]));
            });
        }
    }
    getComponent() {
        const props = {
            columns: this.columns,
            datasource: this.datasource,
        };
        return (_jsx(UIValidation, { error: this.error, children: _jsx(UITableComponent, { ...props }, ulid()) }));
    }
}
//# sourceMappingURL=uiTable.js.map